import React, { PureComponent } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Value } from 'slate'

import Editor from '../Editor'
import createCommentPlugins from '../plugins/createCommentPlugins'
import searchNotes from '../utils/searchNotes'
import searchUsers from '../utils/searchUsers'
import RoutesHelper, { getAbsolutePath } from 'helpers/RoutesHelper'
import { withReduxStoreForwardRef } from 'data/withReduxStore'
import { selectors as NotesSelectors } from 'data/notes'
import { selectors as UsersSelectors } from 'data/users'
import {
  getDefaultValue,
  convertValueToEntities,
  convertValueToText,
} from './utils'

class CommentsEditor extends PureComponent {
  static propTypes = {
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    onCancel: PropTypes.func,
    onEditorRef: PropTypes.func,
    autoFocus: PropTypes.bool,
    reduxStore: PropTypes.object.isRequired,
  }

  static defaultProps = {
    onEditorRef: () => {},
    sendStatus: {},
  }

  constructor(props) {
    super(props)
    this.plugins = createCommentPlugins({
      submit: this.submit,
      cancel: this.onEscape,
      fetchNotes: this.fetchNotes,
      fetchUsers: this.fetchUsers,
      getCurrentUser: this.getCurrentUser,
      getFullNotePath: this.getFullNotePath,
    })
    this.decorator = null

    this.state = {
      value: !props.threadDraft
        ? props.value || getDefaultValue()
        : Value.fromJSON(props.threadDraft),
      oldValue: null,
    }
  }

  resetInput = () => {
    this.setState({ value: getDefaultValue() })
  }

  componentDidUpdate(prevProps) {
    if (!this.props.readOnly && prevProps.readOnly) {
      this.setState({
        oldValue: Value.fromJSON(this.state.value.toJSON()),
      })
      this.select()
    }
    if (prevProps.sendStatus.ongoing && this.props.sendStatus.success) {
      this.setState({
        value: getDefaultValue(),
      })
    }
  }

  onChange = ({ value }) => {
    if (this.props.onDraftChange) {
      this.props.onDraftChange(value.toJSON())
    }
    if (this.props.onChange) {
      const content = convertValueToText(value)
      this.props.onChange(content, value)
    }
    this.setState({ value })
  }

  submit = () => {
    const { value } = this.state
    const content = convertValueToText(value)
    const entities = convertValueToEntities(value)
    if (content) {
      if (this.props.onSubmit) {
        this.props.onSubmit(content, entities)
      }
    }
  }

  focus = () => {
    if (this.editorRef) {
      this.editorRef.focus().moveToEndOfDocument()
    }
  }

  select = () => {
    if (this.editorRef && this.editorRef.editor) {
      this.editorRef.editor.moveToRangeOfDocument().focus()
    }
  }

  onEscape = event => {
    if (this.props.onCancel) {
      this.props.onCancel()
      this.setState({
        value: this.state.oldValue,
      })
    }
  }

  onEditorRef = ref => {
    this.editorRef = ref
    this.props.onEditorRef(ref)
    if (!this.props.readOnly) {
      this.focus()
    }
  }

  fetchUsers = query => {
    return searchUsers(this.props.reduxStore.getState(), query)
  }

  getCurrentUser = () => {
    return UsersSelectors.getCurrentUser(
      this.props.reduxStore.getState()
    ).toJS()
  }

  fetchNotes = query => {
    return searchNotes(this.props.reduxStore.getState(), query, {
      fetchSearchToken: this.props.fetchSearchToken,
    })
  }

  getFullNotePath = noteId => {
    const note = NotesSelectors.getNote(
      this.props.reduxStore.getState(),
      noteId
    )
    if (!note) {
      return null
    }
    const { channelId } = note
    const relativePath = RoutesHelper.noteChannelShow({ channelId, noteId })
    return getAbsolutePath(relativePath)
  }

  render() {
    const {
      autoFocus,
      children,
      containerClassName,
      containerStyle = {},
      editorClassName,
      replier,
    } = this.props

    const placeholder =
      this.props.placeholder !== undefined
        ? this.props.placeholder
        : 'Write a comment'

    return (
      <div className={containerClassName} style={{ ...containerStyle }}>
        <div
          className={classNames(
            editorClassName,
            'shade-10 font-size-small',
            replier ? 'replier-editor' : ''
          )}
        >
          <Editor
            commentEditor
            value={this.state.value}
            readOnly={this.props.readOnly}
            onChange={this.onChange}
            autoFocus={autoFocus}
            placeholder={placeholder}
            plugins={this.plugins}
            onEditorRef={this.onEditorRef}
            onEscape={this.onEscape}
          />
        </div>
        {children && children({ submit: this.submit })}
      </div>
    )
  }
}

export default withReduxStoreForwardRef(CommentsEditor)
